<template>
  <div class="min-h-screen bg-[#FAF8F8] flex flex-col font-sans">
    <!-- Barre de navigation supérieure (Header) -->
    <header class="bg-white/95 backdrop-blur-md shadow-xs sticky top-0 z-30 border-b border-gray-100">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16 items-center">
          <!-- Zone Logo et Nom Officiel GiraafFlow -->
          <div class="flex items-center gap-6">
            <router-link to="/" class="flex items-center gap-2.5 shrink-0 group">
              <img src="/logo.png" alt="GiraafFlow Logo"
                class="h-9 w-auto object-contain transition-transform group-hover:scale-105"
                @error="$event.target.style.display = 'none'" />
              <span class="text-xl font-extrabold text-[#0B1B3D] tracking-tight">
                Giraaf<span
                  class="text-transparent bg-clip-text bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#F97316]">Flow</span>
              </span>
            </router-link>

            <!-- Navigation desktop multilingue -->
            <nav class="hidden md:flex items-center gap-1">
              <router-link v-for="item in navItems" :key="item.to" :to="item.to" :exact-active-class="item.to === '/'
                  ? 'bg-purple-50 text-[#7C3AED] font-semibold'
                  : ''
                " :active-class="item.to !== '/'
                    ? 'bg-purple-50 text-[#7C3AED] font-semibold'
                    : ''
                  "
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs sm:text-sm font-medium text-gray-600 hover:text-[#7C3AED] hover:bg-purple-50/70 transition-all whitespace-nowrap">
                <component :is="item.icon" class="w-4 h-4" />
                {{ item.label }}
              </router-link>
            </nav>
          </div>

          <!-- Zone Droite : Sélecteur Langue + Utilisateur Connecté -->
          <div class="flex items-center gap-3">

            <!-- Sélecteur Langue FR / EN -->
            <div class="flex items-center bg-gray-100 p-0.5 rounded-xl text-[11px] font-bold">
              <button @click="setLanguage('fr')"
                :class="currentLang === 'fr' ? 'bg-white text-[#0B1B3D] shadow-xs' : 'text-gray-400 hover:text-gray-700'"
                class="px-2 py-1 rounded-lg transition duration-150 cursor-pointer">
                FR
              </button>
              <button @click="setLanguage('en')"
                :class="currentLang === 'en' ? 'bg-white text-[#0B1B3D] shadow-xs' : 'text-gray-400 hover:text-gray-700'"
                class="px-2 py-1 rounded-lg transition duration-150 cursor-pointer">
                EN
              </button>
            </div>

            <!-- Utilisateur Desktop -->
            <div v-if="user" class="hidden sm:flex items-center gap-3">
              <div class="flex items-center gap-2 pl-3 pr-2 py-1 rounded-full bg-gray-50 border border-gray-200/80">
                <div style="background: linear-gradient(135deg, #7C3AED 0%, #C026D3 50%, #F97316 100%);"
                  class="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center uppercase shadow-xs">
                  {{ userInitial }}
                </div>
                <span class="text-sm font-semibold text-gray-700 pr-1">
                  {{ user.name }}
                </span>
              </div>
              <button @click="logout"
                class="text-xs font-bold text-rose-500 hover:text-white bg-rose-50 hover:bg-rose-500 px-3.5 py-2 rounded-xl transition-all cursor-pointer">
                {{ t.logout }}
              </button>
            </div>

            <!-- Bouton menu mobile -->
            <button @click="mobileMenuOpen = !mobileMenuOpen"
              class="md:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label="Ouvrir le menu">
              <svg v-if="!mobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Navigation mobile (dépliable) -->
        <transition name="fade">
          <nav v-if="mobileMenuOpen" class="md:hidden pb-4 flex flex-col gap-1 border-t border-gray-100 pt-2">
            <router-link v-for="item in navItems" :key="item.to" :to="item.to" @click="mobileMenuOpen = false"
              :exact-active-class="item.to === '/'
                  ? 'bg-purple-50 text-[#7C3AED] font-semibold'
                  : ''
                " :active-class="item.to !== '/'
                  ? 'bg-purple-50 text-[#7C3AED] font-semibold'
                  : ''
                "
              class="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-gray-600 hover:text-[#7C3AED] hover:bg-purple-50/70 transition-colors">
              <component :is="item.icon" class="w-4 h-4" />
              {{ item.label }}
            </router-link>

            <div v-if="user" class="flex items-center justify-between mt-2 pt-3 border-t border-gray-100">
              <div class="flex items-center gap-2">
                <div style="background: linear-gradient(135deg, #7C3AED 0%, #C026D3 50%, #F97316 100%);"
                  class="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center uppercase">
                  {{ userInitial }}
                </div>
                <span class="text-sm font-semibold text-gray-700">{{
                  user.name
                  }}</span>
              </div>
              <button @click="logout"
                class="text-xs font-bold text-rose-500 hover:text-white bg-rose-50 hover:bg-rose-500 px-3 py-2 rounded-xl transition-colors">
                {{ t.logout }}
              </button>
            </div>
          </nav>
        </transition>
      </div>
    </header>

    <!-- Zone d'affichage des vues dynamiques -->
    <main class="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 grow w-full">
      <router-view />
    </main>

    <!-- Pied de page (Footer) officiel SaaS GiraafFlow -->
    <footer class="bg-[#0B1B3D] text-gray-300 pt-16 pb-10 border-t border-slate-800">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 pb-12 border-b border-slate-800/80">
          <!-- Colonne 1 : Marque & Pitch Produit -->
          <div class="lg:col-span-2 space-y-4">
            <div class="flex items-center gap-3">
              <img src="/logo.png" alt="GiraafFlow Logo" class="h-9 w-auto" />
              <span class="text-xl font-extrabold text-white tracking-tight">
                Giraaf<span
                  class="text-transparent bg-clip-text bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#F97316]">Flow</span>
              </span>
            </div>
            <p class="text-sm text-gray-400 max-w-sm leading-relaxed">
              {{ t.pitch }}
            </p>
            <div
              class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900/90 border border-slate-700/60 text-xs text-emerald-400">
              <span class="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
              {{ t.statusOperational }}
            </div>
          </div>

          <!-- Colonne 2 : Fonctionnalités SaaS -->
          <div class="space-y-3">
            <h4 class="text-xs font-bold uppercase tracking-wider text-white">
              {{ t.colProduct }}
            </h4>
            <ul class="space-y-2.5 text-sm text-gray-400">
              <li>
                <router-link to="/" class="hover:text-[#F97316] transition">{{ t.home }}</router-link>
              </li>
              <li>
                <router-link to="/projects" class="hover:text-[#F97316] transition">{{ t.projects }}</router-link>
              </li>
              <li>
                <router-link to="/kanban" class="hover:text-[#F97316] transition font-medium">{{ t.kanban
                  }}</router-link>
              </li>
              <li>
                <router-link to="/comptes" class="hover:text-[#F97316] transition">{{ t.accounts }}</router-link>
              </li>
            </ul>
          </div>

          <!-- Colonne 3 : Écosystème SaaS Giraaf Park -->
          <div class="space-y-3">
            <h4 class="text-xs font-bold uppercase tracking-wider text-white">
              Écosystème
            </h4>
            <ul class="space-y-2.5 text-sm text-gray-400">
              <li>
                <a href="https://chat.giraafpark.com" target="_blank" rel="noopener noreferrer"
                  class="hover:text-[#7C3AED] transition flex items-center gap-1.5">
                  GiraafChat
                  <span class="text-[10px] px-1.5 py-0.5 rounded bg-purple-900/50 text-[#C026D3] font-bold">IA</span>
                </a>
              </li>
              <li>
                <span class="text-gray-400">GiraafProspect</span>
              </li>
              <li>
                <span class="text-gray-400">GiraafDesk</span>
              </li>
              <li>
                <a href="https://giraafpark.com" target="_blank" rel="noopener noreferrer"
                  class="hover:text-white transition">Giraaf Park Hub</a>
              </li>
            </ul>
          </div>

          <!-- Colonne 4 : Support & Aide -->
          <div class="space-y-3">
            <h4 class="text-xs font-bold uppercase tracking-wider text-white">
              Ressources
            </h4>
            <ul class="space-y-2.5 text-sm text-gray-400">
              <li>
                <router-link to="/aide" class="hover:text-[#F97316] transition">{{ t.help }}</router-link>
              </li>
              <li>
                <router-link to="/about" class="hover:text-[#F97316] transition">{{ t.about }}</router-link>
              </li>
              <li>
                <router-link to="/maintenance" class="hover:text-[#F97316] transition">{{ t.status }}</router-link>
              </li>
              <li>
                <a href="mailto:growth@giraafpark.com" class="hover:text-[#F97316] transition">Support technique</a>
              </li>
            </ul>
          </div>
        </div>

        <!-- Barre inférieure (Copyright & Liens Légaux) -->
        <div class="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <p>
            © 2026 GiraafFlow — Une application de la suite Giraaf Park.
          </p>

          <div class="flex flex-wrap items-center gap-6">
            <router-link to="/mentions-legales" class="hover:text-gray-300 transition">Mentions Légales</router-link>
            <router-link to="/confidentialite" class="hover:text-gray-300 transition">Confidentialité</router-link>
            <router-link to="/cgu" class="hover:text-gray-300 transition">CGU</router-link>
            <router-link to="/cgv" class="hover:text-gray-300 transition">CGV</router-link>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { h } from 'vue'
import apiClient from '@/services/apiClient'

// Dictionnaire bilingue FR / EN
const TRANSLATIONS = {
  fr: {
    home: 'Accueil',
    projects: 'Projets',
    kanban: 'Kanban',
    accounts: 'Comptes',
    status: 'Maintenance',
    help: 'Aide & Support',
    about: 'À propos',
    logout: 'Déconnexion',
    colProduct: 'Produit',
    pitch: "La plateforme tout-en-un conçue pour planifier, exécuter et automatiser vos projets d'équipe en toute simplicité.",
    statusOperational: 'Systèmes 100% opérationnels'
  },
  en: {
    home: 'Dashboard',
    projects: 'Projects',
    kanban: 'Kanban',
    accounts: 'Accounts',
    status: 'Status',
    help: 'Help & Support',
    about: 'About',
    logout: 'Logout',
    colProduct: 'Product',
    pitch: 'The all-in-one platform designed to plan, execute and automate your team projects with total ease.',
    statusOperational: 'Systems 100% Operational'
  }
}

// Icônes SVG optimisées (Render Functions)
const IconHome = () =>
  h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }, [
    h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: 'M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1h3a1 1 0 001-1V10' })
  ])

const IconFolder = () =>
  h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }, [
    h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: 'M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z' })
  ])

const IconKanban = () =>
  h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }, [
    h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: 'M9 4H4v16h5V4zm11 0h-5v10h5V4zm-5 12h5v4h-5v-4z' })
  ])

const IconUsers = () =>
  h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }, [
    h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: 'M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m5-4a4 4 0 100-8 4 4 0 000 8zm6 4a4 4 0 10-8 0' })
  ])

const IconTool = () =>
  h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }, [
    h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z' })
  ])

const IconHelp = () =>
  h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }, [
    h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: 'M9.09 9a3 3 0 115.83 1c0 2-3 2-3 4M12 17h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' })
  ])

const IconInfo = () =>
  h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }, [
    h('circle', { cx: '12', cy: '12', r: '10' }),
    h('line', { x1: '12', y1: '16', x2: '12', y2: '12' }),
    h('line', { x1: '12', y1: '8', x2: '12.01', y2: '8' })
  ])

export default {
  name: 'AppLayout',
  components: { IconHome, IconFolder, IconKanban, IconUsers, IconTool, IconHelp, IconInfo },
  data() {
    return {
      currentLang: localStorage.getItem('giraaf_lang') || 'fr',
      user: null,
      mobileMenuOpen: false
    }
  },
  computed: {
    t() {
      return TRANSLATIONS[this.currentLang] || TRANSLATIONS.fr
    },
    navItems() {
      return [
        { to: '/', label: this.t.home, icon: IconHome },
        { to: '/projects', label: this.t.projects, icon: IconFolder },
        { to: '/kanban', label: this.t.kanban, icon: IconKanban },
        { to: '/comptes', label: this.t.accounts, icon: IconUsers },
        { to: '/maintenance', label: this.t.status, icon: IconTool },
        { to: '/aide', label: this.t.help, icon: IconHelp },
        { to: '/about', label: this.t.about, icon: IconInfo }
      ]
    },
    userInitial() {
      return this.user?.name ? this.user.name.charAt(0) : '?'
    }
  },
  async created() {
    try {
      const response = await apiClient.get('/api/v1/dashboard')
      if (response.data.current_user) {
        this.user = response.data.current_user
      }
    } catch (error) {
      console.error('Session expirée ou non connecté')
    }
  },
  methods: {
    setLanguage(lang) {
      this.currentLang = lang
      localStorage.setItem('giraaf_lang', lang)
    },
    logout() {
      localStorage.removeItem('token')
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
